/**
 * Top navigation search for client-side navigation items.
 * It searches configured left-nav menu entries and routes users without backend calls.
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TopNavDeepSearchResult } from '@modules/navigation/models/top-nav-deep-search.model';
import { TopNavDeepSearchService } from '@modules/navigation/services/top-nav-deep-search.service';
import { Subject, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    takeUntil,
} from 'rxjs/operators';
import { LEFT_NAV_ITEMS } from '../../data/left-nav.data';
import { LeftNavItem, LeftNavSubItem } from '../../models/left-nav-item.model';

interface TopNavSearchResult {
    id: string;
    label: string;
    route: string | null;
    action?: LeftNavSubItem['action'];
    disabled: boolean;
    searchable: string;
}

@Component({
    selector: 'sbpro-top-nav-search',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './top-nav-search.component.html',
    styleUrls: ['top-nav-search.component.scss'],
})
export class TopNavSearchComponent implements OnInit, OnDestroy {
    query = '';
    showDropdown = false;
    navigationResultsFiltered: TopNavSearchResult[] = [];
    deepSearchResults: TopNavDeepSearchResult[] = [];

    private readonly navigationResults = this.buildResults(LEFT_NAV_ITEMS);
    private readonly destroy$ = new Subject<void>();
    private readonly deepSearchTerm$ = new Subject<string>();

    constructor(
        private readonly router: Router,
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly topNavDeepSearchService: TopNavDeepSearchService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.deepSearchTerm$
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap((term) => {
                    if (term.length < 2) {
                        return of([] as TopNavDeepSearchResult[]);
                    }
                    return this.topNavDeepSearchService.search(term).pipe(
                        catchError(() => of([] as TopNavDeepSearchResult[]))
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((results) => {
                this.deepSearchResults = results;
                this.showDropdown = this.shouldShowDropdown();
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onInput(value: string): void {
        this.query = value;
        const normalized = value.trim().toLowerCase();
        if (normalized.length < 2) {
            this.clearResults();
            return;
        }

        this.navigationResultsFiltered = this.navigationResults.filter((item) =>
            item.searchable.includes(normalized)
        );
        this.showDropdown = this.shouldShowDropdown();
        this.deepSearchTerm$.next(normalized);
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.clearSearch();
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const firstNavigationResult = this.navigationResultsFiltered.find(
                (item) => !item.disabled && !!item.route
            );
            if (firstNavigationResult) {
                this.selectResult(firstNavigationResult);
                return;
            }

            const firstDeepResult = this.deepSearchResults[0];
            if (firstDeepResult) {
                this.selectDeepSearchResult(firstDeepResult);
            }
        }
    }

    selectResult(result: TopNavSearchResult): void {
        if (result.disabled || !result.route) {
            return;
        }
        if (result.action) {
            void this.router.navigate([result.route], { queryParams: { navAction: result.action } });
        } else {
            void this.router.navigate([result.route]);
        }
        this.clearSearch();
    }

    selectDeepSearchResult(result: TopNavDeepSearchResult): void {
        void this.router.navigate([result.route], {
            queryParams: result.queryParams,
        });
        this.clearSearch();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as Node | null;
        if (!target) {
            return;
        }
        if (!this.elementRef.nativeElement.contains(target)) {
            this.clearSearch();
        }
    }

    private closeDropdown(): void {
        this.showDropdown = false;
    }

    private clearSearch(): void {
        this.query = '';
        this.clearResults();
        this.closeDropdown();
    }

    private clearResults(): void {
        this.navigationResultsFiltered = [];
        this.deepSearchResults = [];
    }

    private shouldShowDropdown(): boolean {
        return (
            this.navigationResultsFiltered.length > 0 || this.deepSearchResults.length > 0
        );
    }

    private buildResults(items: LeftNavItem[]): TopNavSearchResult[] {
        const results: TopNavSearchResult[] = [];
        items.forEach((parent) => {
            results.push({
                id: parent.id,
                label: parent.label,
                route: parent.route,
                disabled: !parent.route,
                searchable: parent.label.toLowerCase(),
            });

            parent.children?.forEach((child) => {
                const combinedLabel = `${parent.label} > ${child.label}`;
                results.push({
                    id: `${parent.id}-${child.id}`,
                    label: combinedLabel,
                    route: child.route,
                    action: child.action,
                    disabled: !child.route,
                    searchable: `${parent.label} ${child.label} ${combinedLabel}`.toLowerCase(),
                });
            });
        });

        return results;
    }
}
